<script setup>
// Buttons of a WhatsApp template: "reply buttons" (QUICK_REPLY) and "action buttons" (URL, phone,
// copy code), as two ordered groups. Meta requires quick replies to sit together, before or after the
// rest, so the two groups ARE that rule; the swap button chooses which comes first.
// Plan: docs/smarticks/whatsapp-template-builder-plan.md §3 K1–K8, UI/UX specs.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  BUTTON_CAPS,
  BUTTONS_MAX,
  variableTokens,
} from 'dashboard/helper/whatsappTemplateRules';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ order: 'qr_first', quick_replies: [], actions: [] }),
  },
  category: { type: String, default: 'UTILITY' },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const value = computed(() => props.modelValue);
const total = computed(
  () => value.value.quick_replies.length + value.value.actions.length
);
const countOf = type =>
  type === 'QUICK_REPLY'
    ? value.value.quick_replies.length
    : value.value.actions.filter(action => action.type === type).length;
const canAdd = type =>
  total.value < BUTTONS_MAX &&
  countOf(type) < BUTTON_CAPS[type] &&
  (type !== 'COPY_CODE' || props.category === 'MARKETING');

const update = changes => emit('update:modelValue', { ...value.value, ...changes });

const addReply = () =>
  update({ quick_replies: [...value.value.quick_replies, { text: '' }] });
const addAction = type => {
  const base = { type };
  if (type === 'URL') Object.assign(base, { text: '', url: '', example: '' });
  if (type === 'PHONE_NUMBER') Object.assign(base, { text: '', phone_number: '' });
  if (type === 'COPY_CODE') Object.assign(base, { example: '' });
  update({ actions: [...value.value.actions, base] });
};

const editItem = (group, index, changes) => {
  const list = [...value.value[group]];
  list[index] = { ...list[index], ...changes };
  update({ [group]: list });
};
const removeItem = (group, index) =>
  update({ [group]: value.value[group].filter((_, i) => i !== index) });
const moveItem = (group, index, delta) => {
  const list = [...value.value[group]];
  const target = index + delta;
  if (target < 0 || target >= list.length) return;
  [list[index], list[target]] = [list[target], list[index]];
  update({ [group]: list });
};
const changeActionType = (index, type) => {
  if (!type) return;
  const list = [...value.value.actions];
  list[index] = { type, text: list[index].text || '' };
  if (type === 'URL') Object.assign(list[index], { url: '', example: '' });
  if (type === 'PHONE_NUMBER') list[index].phone_number = '';
  if (type === 'COPY_CODE') list[index] = { type, example: '' };
  update({ actions: list });
};
const swapOrder = () =>
  update({ order: value.value.order === 'qr_first' ? 'qr_last' : 'qr_first' });

const typeOptions = current =>
  [
    { value: 'URL', label: t('WA_TEMPLATES.BUILDER.BUTTONS.TYPE_URL') },
    {
      value: 'PHONE_NUMBER',
      label: t('WA_TEMPLATES.BUILDER.BUTTONS.TYPE_PHONE'),
    },
    { value: 'COPY_CODE', label: t('WA_TEMPLATES.BUILDER.BUTTONS.TYPE_COPY') },
  ].filter(option => option.value === current || canAdd(option.value));

const urlHasVariable = url => variableTokens(url).length > 0;
const counter = (text, max) => `${[...(text || '')].length}/${max}`;

const groups = computed(() =>
  value.value.order === 'qr_first'
    ? ['quick_replies', 'actions']
    : ['actions', 'quick_replies']
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-xs text-n-slate-11">
      {{ t('WA_TEMPLATES.BUILDER.BUTTONS.LIMITS') }}
    </p>
    <template v-for="(group, groupIndex) in groups" :key="group">
      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-0.5">
          <p class="text-sm font-medium text-n-slate-12">
            {{
              group === 'quick_replies'
                ? t('WA_TEMPLATES.BUILDER.BUTTONS.QR_GROUP')
                : t('WA_TEMPLATES.BUILDER.BUTTONS.ACTION_GROUP')
            }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              group === 'quick_replies'
                ? t('WA_TEMPLATES.BUILDER.BUTTONS.QR_HINT')
                : t('WA_TEMPLATES.BUILDER.BUTTONS.ACTION_HINT')
            }}
          </p>
        </div>

        <div
          v-for="(item, index) in value[group]"
          :key="`${group}-${index}`"
          class="rounded-lg outline outline-1 -outline-offset-1 outline-n-weak p-3 flex flex-col gap-3"
        >
          <div class="flex flex-wrap items-center gap-2">
            <div v-if="group === 'actions'" class="flex-1 min-w-0">
              <ComboBox
                :model-value="item.type"
                :options="typeOptions(item.type)"
                class="[&>div>button]:bg-n-alpha-black2"
                @update:model-value="changeActionType(index, $event)"
              />
            </div>
            <span v-else class="flex-1 text-sm text-n-slate-11">{{
              t('WA_TEMPLATES.BUILDER.BUTTONS.QR_ITEM', { n: index + 1 })
            }}</span>
            <NextButton
              ghost
              slate
              icon="i-lucide-arrow-up"
              :disabled="index === 0"
              :aria-label="t('WA_TEMPLATES.BUILDER.BUTTONS.MOVE_UP')"
              @click="moveItem(group, index, -1)"
            />
            <NextButton
              ghost
              slate
              icon="i-lucide-arrow-down"
              :disabled="index === value[group].length - 1"
              :aria-label="t('WA_TEMPLATES.BUILDER.BUTTONS.MOVE_DOWN')"
              @click="moveItem(group, index, 1)"
            />
            <NextButton
              ghost
              slate
              icon="i-lucide-trash-2"
              class="hover:enabled:text-n-ruby-11"
              :aria-label="t('WA_TEMPLATES.BUILDER.BUTTONS.REMOVE')"
              @click="removeItem(group, index)"
            />
          </div>

          <Input
            v-if="item.type !== 'COPY_CODE'"
            :model-value="item.text"
            dir="auto"
            :label="t('WA_TEMPLATES.BUILDER.BUTTONS.LABEL')"
            :message="counter(item.text, 25)"
            @update:model-value="editItem(group, index, { text: $event })"
          />

          <template v-if="item.type === 'URL'">
            <Input
              :model-value="item.url"
              type="url"
              custom-input-class="[direction:ltr] font-inter"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.URL')"
              :message="t('WA_TEMPLATES.BUILDER.BUTTONS.URL_HINT')"
              placeholder="https://example.com/order?id={{1}}"
              @update:model-value="editItem(group, index, { url: $event })"
            />
            <Input
              v-if="urlHasVariable(item.url)"
              :model-value="item.example"
              custom-input-class="[direction:ltr] font-inter"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.URL_EXAMPLE')"
              @update:model-value="editItem(group, index, { example: $event })"
            />
          </template>

          <Input
            v-if="item.type === 'PHONE_NUMBER'"
            :model-value="item.phone_number"
            type="tel"
            custom-input-class="[direction:ltr] font-inter"
            :label="t('WA_TEMPLATES.BUILDER.BUTTONS.PHONE')"
            :message="t('WA_TEMPLATES.BUILDER.BUTTONS.PHONE_HINT')"
            @update:model-value="
              editItem(group, index, { phone_number: $event })
            "
          />

          <template v-if="item.type === 'COPY_CODE'">
            <p class="text-sm text-n-slate-11">
              {{ t('WA_TEMPLATES.BUILDER.BUTTONS.COPY_FIXED') }}
            </p>
            <Input
              :model-value="item.example"
              custom-input-class="[direction:ltr] font-inter"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.COPY_EXAMPLE')"
              :message="t('WA_TEMPLATES.BUILDER.BUTTONS.COPY_HINT')"
              @update:model-value="editItem(group, index, { example: $event })"
            />
          </template>
        </div>

        <div class="flex flex-wrap gap-2">
          <template v-if="group === 'quick_replies'">
            <NextButton
              v-if="canAdd('QUICK_REPLY')"
              ghost
              slate
              sm
              icon="i-lucide-plus"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.ADD_QR')"
              @click="addReply"
            />
          </template>
          <template v-else>
            <NextButton
              v-if="canAdd('URL')"
              ghost
              slate
              sm
              icon="i-lucide-plus"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.ADD_URL')"
              @click="addAction('URL')"
            />
            <NextButton
              v-if="canAdd('PHONE_NUMBER')"
              ghost
              slate
              sm
              icon="i-lucide-plus"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.ADD_PHONE')"
              @click="addAction('PHONE_NUMBER')"
            />
            <NextButton
              v-if="canAdd('COPY_CODE')"
              ghost
              slate
              sm
              icon="i-lucide-plus"
              :label="t('WA_TEMPLATES.BUILDER.BUTTONS.ADD_COPY')"
              @click="addAction('COPY_CODE')"
            />
          </template>
        </div>
      </div>

      <div v-if="groupIndex === 0" class="flex justify-center">
        <NextButton
          ghost
          slate
          sm
          icon="i-lucide-arrow-up-down"
          :label="t('WA_TEMPLATES.BUILDER.BUTTONS.SWAP')"
          @click="swapOrder"
        />
      </div>
    </template>
    <p v-if="total >= 10" class="text-xs text-n-slate-11">
      {{ t('WA_TEMPLATES.BUILDER.BUTTONS.MAX_REACHED') }}
    </p>
  </div>
</template>
